<template>
    <div>
        <Breadcrumbs :type="type" :header="title" :url="url"></Breadcrumbs>
        <div class="flex items-center justify-between mb-6">
            <search-filter v-model="form.search" class="mr-4 w-full " @reset="reset">
                <template v-slot:filter>
                    <select v-model="form.trashed" class="form-select w-28">
                        <option :value="null">Filter</option>
                        <option value="with">With Trashed</option>
                        <option value="only">Only Trashed</option>
                    </select>
                </template>
                <template v-slot:page>
                    <select v-model="form.length" class="form-select w-20">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="75">75</option>
                        <option value="100">100</option>
                        <option value="150">150</option>
                        <option value="200">200</option>
                    </select>
                    <Button :type="'reset'" :method="reset" class="ml-2"></Button>
                </template>
                <template v-slot:status>
                    <div class="relative w-56" ref="dropdownRef">
                        <input type="text" v-model="ministrySearch" @focus="isMinistryOpen = true" @blur="handleBlur"
                            placeholder="Search ministry..." class="form-input w-56" />
                        <button v-if="ministrySearch" @click="clearSelection"
                            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <i class="fas fa-times text-[#C70833]"></i>
                        </button>
                        <div v-show="isMinistryOpen"
                            class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            <div class="p-2 hover:bg-gray-100 cursor-pointer text-gray-600"
                                @mousedown.prevent="selectMinistry(null)">
                                Select Implementing Ministry
                            </div>
                            <div v-for="ministry in filteredMinistries" :key="ministry.id"
                                @mousedown.prevent="selectMinistry(ministry)"
                                class="p-2 hover:bg-gray-100 cursor-pointer"
                                :class="{ 'bg-indigo-50': form.ministry === ministry.id }">
                                {{ ministry.ministry_name }}
                            </div>
                            <div v-if="filteredMinistries.length === 0" class="p-2 text-gray-500 text-center">
                                No results found
                            </div>
                        </div>
                    </div>
                    <div class="relative w-56" ref="proposingDropdownRef">
                        <input type="text" v-model="proposingMinistrySearch" @focus="isProposingMinistryOpen = true" @blur="handleProposingBlur"
                            placeholder="Search proposing body..." class="form-input w-full" />
                        <button v-if="proposingMinistrySearch" @click="clearProposingSelection"
                            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <i class="fas fa-times text-[#C70833]"></i>
                        </button>
                        <div v-show="isProposingMinistryOpen"
                            class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            <div class="p-2 hover:bg-gray-100 cursor-pointer text-gray-600"
                                @mousedown.prevent="selectProposingMinistry(null)">
                                Select Proposing Body
                            </div>
                            <div v-for="ministry in filteredProposingMinistries" :key="ministry.id"
                                @mousedown.prevent="selectProposingMinistry(ministry)"
                                class="p-2 hover:bg-gray-100 cursor-pointer"
                                :class="{ 'bg-indigo-50': form.proposing_ministry === ministry.id }">
                                {{ ministry.ministry_name }}
                            </div>
                            <div v-if="filteredProposingMinistries.length === 0" class="p-2 text-gray-500 text-center">
                                No results found
                            </div>
                        </div>
                    </div>
                    <select v-model="form.fiscal_year" class="form-select w-32">
                        <option :value="null">Select Fiscal Year</option>
                        <option v-for="fiscal_year in fiscal_years" :key="fiscal_year.id" :value="fiscal_year.id">
                            {{ fiscal_year.name }}
                        </option>
                    </select>
                    <select v-model="form.status" class="form-select w-24">
                        <option :value="null">Status</option>
                        <option value="1">Active</option>
                        <option value="2">Inactive</option>
                    </select>
                </template>
            </search-filter>
            <Button :type="type" :name="title" :url="createUrl" v-if="permission.add > 0"></Button>
        </div>
        <div class="bg-white rounded-md shadow overflow-hidden">
            <table class="w-full whitespace-nowrap">
                <thead>
                    <tr class="text-left bg-gray-50 border-b border-gray-200">
                        <th class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" width="20">
                            <i class="fas fa-hashtag mr-2"></i>Sl.
                        </th>
                        <th class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <i class="fas fa-info-circle mr-2"></i>Project Info
                        </th>
                        <th class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                            <i class="fas fa-check-circle mr-2"></i>Status
                        </th>
                        <th class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                            <i class="fas fa-cogs mr-2"></i>Action
                        </th>
                        <th class="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                            <input type="checkbox" @click="selectAll" v-model="allSelected"
                                class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(project, index) in projects.data" :key="project.id"
                        class="hover:bg-gray-50 focus-within:bg-gray-50 transition duration-150 ease-in-out">
                        <td class="px-6 py-4 text-center text-sm">
                            <span class="bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                {{ index + projects.from }}
                            </span>
                        </td>
                        <td class="px-6 py-4">
                            <div class="space-y-2">
                                <div class="flex items-center">
                                    <i class="fas fa-project-diagram text-indigo-600 mr-2"></i>
                                    <p class="text-sm font-medium text-gray-900">{{ project.project_name }}</p>
                                    <icon v-if="project.deleted_at" name="trash" class="ml-2 w-4 h-4 text-red-500" />
                                </div>
                                <div class="text-sm text-gray-600 space-y-1">
                                    <p class="flex items-center">
                                        <i class="fas fa-cog mr-2 w-5 text-gray-400"></i>
                                        <span class="font-medium">Service:</span>
                                        <span class="ml-1">{{ project.service_name }} {{ project.sub_service_name ?
                                            `[${project.sub_service_name}]` : '' }}</span>
                                    </p>
                                    <p class="flex items-center">
                                        <i class="fas fa-building mr-2 w-5 text-gray-400"></i>
                                        <span class="font-medium">Proposing Body:</span>
                                        <span class="ml-1">{{ project.proposing_ministry }}</span>
                                    </p>
                                    <p class="flex items-center">
                                        <i class="fas fa-tasks mr-2 w-5 text-gray-400"></i>
                                        <span class="font-medium">Implementing Ministry:</span>
                                        <span class="ml-1">{{ project.implementing_ministry }}</span>
                                    </p>
                                    <p class="flex items-center">
                                        <i class="fas fa-money-bill-wave mr-2 w-5 text-gray-400"></i>
                                        <span class="font-medium">Amount:</span>
                                        <span class="ml-1">{{ project.project_amount }}</span>
                                        <span class="mx-2">|</span>
                                        <span class="font-medium">Remain:</span>
                                        <span class="ml-1">{{ project.remain_project_amount ?
                                            project.remain_project_amount : 0
                                        }}</span>
                                    </p>
                                    <p class="flex items-center">
                                        <i class="fas fa-calendar-alt mr-2 w-5 text-gray-400"></i>
                                        <span class="font-medium">Fiscal year:</span>
                                        <span class="ml-1">{{ project.fiscal_year }}</span>
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 text-center">
                            <status-level :status="project.status"></status-level>
                        </td>
                        <td class="px-6 py-4 text-center">
                            <div class="flex items-center justify-center space-x-3" v-if="permission.edit > 0">
                                <Link class="text-indigo-600 hover:text-indigo-900 transition duration-150 ease-in-out"
                                    :href="`/backend/projects/${project.id}/edit`" tabindex="-1">
                                <i class="fas fa-edit text-lg"></i>
                                </Link>
                                <button @click="openmodal(project.id)"
                                    class="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
                                    tabindex="0">₱<span>Update Amount</span>
                                </button>
                                <button @click="openHistoryModal(project.id)"
                                    class="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-full text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:shadow-outline-gray active:bg-gray-700 transition duration-150 ease-in-out"
                                    tabindex="0">
                                    <i class="fas fa-history mr-1"></i>
                                    <span>History</span>
                                </button>
                            </div>
                        </td>
                        <td class="px-6 py-4 text-center">
                            <input type="checkbox" v-model="selectedIds" @click="select" :value="project.id"
                                class="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                        </td>
                    </tr>
                    <tr v-if="projects.data.length === 0">
                        <td class="px-6 py-4 text-center text-gray-500" colspan="5">
                            <i class="fas fa-folder-open mr-2"></i>No project found.
                        </td>
                    </tr>
                    <tr v-if="selectedIds.length > 0">
                        <td class="px-6 py-4" colspan="5">
                            <Button :name="'Status Change'" :class="'btn-indigo ml-auto'" :method="statusChange"
                                :icon="'fa-solid fa-check'"></Button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <pagination class="mt-6" :links="projects.links" />
        <Amount :project_id="project_id" :amountopen="amountopen" @UpdateAmountPopup="update_amount_popup"
            :project_name="project_name"></Amount>
        <History :projectId="project_id" :historyOpen="historyOpen" @updateHistoryPopup="update_history_popup">
        </History>
    </div>
</template>

<script>
    import axios from "axios"
    import { ref, toRefs, inject, computed, onMounted, onUnmounted, watch } from "vue";
    import { Head, Link } from "@inertiajs/inertia-vue3";
    import Icon from "@/Shared/Icon";
    import pickBy from "lodash/pickBy";
    import Layout from "@/Shared/Layout";
    import throttle from "lodash/throttle";
    import mapValues from "lodash/mapValues";
    import Pagination from "@/Shared/Pagination";
    import SearchFilter from "@/Shared/SearchFilter";
    import Breadcrumbs from "@/Shared/Breadcrumbs";
    import StatusLevel from "@/Shared/StatusLevel";
    import Button from "@/Shared/Button";
    import ToolTip from "@/Shared/ToolTip";
    import Amount from "./components/Amount";
    import History from "./components/History";
    import SearchFilterNew from '@/Shared/SearchFilterNew';

    export default {
        components: {
            Head,
            Icon,
            Link,
            Pagination,
            SearchFilter,
            Breadcrumbs,
            StatusLevel,
            Button,
            ToolTip,
            Amount,
            History,
            SearchFilterNew
        },
        layout: Layout,
        props: {
            filters: Object,
            permission: Object,
            projects: Object,
            ministries: Array,
            fiscal_years: Array,
        },
        setup(props) {
            const { filters, projects, permission, ministries, fiscal_years } = toRefs(props);
            const form = ref({
                search: filters.value.search,
                trashed: filters.value.trashed,
                length: filters.value.length ? filters.value.length : 15,
                status: filters.value.status,
                ministry: filters.value.ministry,
                proposing_ministry: filters.value.proposing_ministry,
                fiscal_year: filters.value.fiscal_year,
            });
            const swal = inject("$swal");
            const backendPrefix = ref(process.env.MIX_ADMIN_PREFIX)
            const type = ref("index");
            const title = ref("Projects");
            const url = ref("");
            const createUrl = ref("/backend/projects/create");
            const reset = () => {
                form.value.search = null;
                form.value.trashed = null;
                form.value.length = 15;
                form.value.status = null;
                form.value.ministry = null;
                form.value.proposing_ministry = null;
                form.value.fiscal_year = null;
            };
            const amountopen = ref(false);
            const historyOpen = ref(false);
            const project_id = ref(0);
            const project_name = ref("");
            const ministrySearch = ref("");
            const isMinistryOpen = ref(false);
            const dropdownRef = ref(null);
            const proposingMinistrySearch = ref("");
            const isProposingMinistryOpen = ref(false);
            const proposingDropdownRef = ref(null);

            const handleClickOutside = (event) => {
                if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
                    isMinistryOpen.value = false;
                    initializeMinistrySearch();
                }
                if (proposingDropdownRef.value && !proposingDropdownRef.value.contains(event.target)) {
                    isProposingMinistryOpen.value = false;
                    initializeProposingMinistrySearch();
                }
            };

            // Set up click outside listener
            onMounted(() => {
                document.addEventListener('click', handleClickOutside);
            });

            // Clean up the event listener
            onUnmounted(() => {
                document.removeEventListener('click', handleClickOutside);
            });

            const handleBlur = () => {
                // Keep the dropdown open on blur to allow for clicking options
                setTimeout(() => {
                    if (!document.activeElement?.closest('.relative')) {
                        initializeMinistrySearch();
                    }
                }, 200);
            };

            const clearSelection = () => {
                form.value.ministry = null;
                ministrySearch.value = "";
                isMinistryOpen.value = false;
            };

            // Initialize ministrySearch with selected ministry name
            const initializeMinistrySearch = () => {
                if (form.value.ministry) {
                    const selectedMinistry = ministries.value.find(m => m.id === form.value.ministry);
                    if (selectedMinistry) {
                        ministrySearch.value = selectedMinistry.ministry_name;
                    }
                }
            };

            // Watch for changes in form.ministry
            watch(() => form.value.ministry, (newValue) => {
                if (!newValue) {
                    ministrySearch.value = "";
                }
            });

            // Initialize on mount
            onMounted(() => {
                initializeMinistrySearch();
                initializeProposingMinistrySearch();
            });

            const filteredMinistries = computed(() => {
                if (!ministrySearch.value) return ministries.value;
                return ministries.value.filter(ministry =>
                    ministry.ministry_name.toLowerCase().includes(ministrySearch.value.toLowerCase())
                );
            });

            const selectMinistry = (ministry) => {
                form.value.ministry = ministry ? ministry.id : null;
                ministrySearch.value = ministry ? ministry.ministry_name : "";
                isMinistryOpen.value = false;
            };

            const handleProposingBlur = () => {
                setTimeout(() => {
                    if (!document.activeElement?.closest('.relative')) {
                        initializeProposingMinistrySearch();
                    }
                }, 200);
            };

            const clearProposingSelection = () => {
                form.value.proposing_ministry = null;
                proposingMinistrySearch.value = "";
                isProposingMinistryOpen.value = false;
            };

            const initializeProposingMinistrySearch = () => {
                if (form.value.proposing_ministry) {
                    const selectedMinistry = ministries.value.find(m => m.id === form.value.proposing_ministry);
                    if (selectedMinistry) {
                        proposingMinistrySearch.value = selectedMinistry.ministry_name;
                    }
                }
            };

            const filteredProposingMinistries = computed(() => {
                if (!proposingMinistrySearch.value) return ministries.value;
                return ministries.value.filter(ministry =>
                    ministry.ministry_name.toLowerCase().includes(proposingMinistrySearch.value.toLowerCase())
                );
            });

            const selectProposingMinistry = (ministry) => {
                form.value.proposing_ministry = ministry ? ministry.id : null;
                proposingMinistrySearch.value = ministry ? ministry.ministry_name : "";
                isProposingMinistryOpen.value = false;
            };

            // Watch for changes in form.proposing_ministry
            watch(() => form.value.proposing_ministry, (newValue) => {
                if (!newValue) {
                    proposingMinistrySearch.value = "";
                }
            });

            // Initialization is handled in the mounted hook above

            const openmodal = (id) => {
                project_id.value = id
                if (project_id.value > 0) {
                    amountopen.value = true
                    project_name.value = projects.value.data.find(p => p.id === project_id.value)?.project_name
                }
            }

            const openHistoryModal = (id) => {
                project_id.value = id
                if (project_id.value > 0) {
                    historyOpen.value = true
                }
            }

            const update_amount_popup = (val) => {
                amountopen.value = val
            }

            const update_history_popup = (val) => {
                historyOpen.value = val
            }
            const selected = ref([])
            const allSelected = ref(false)
            const selectedIds = ref([])
            const selectAll = () => {
                selectedIds.value = [];
                if (allSelected.value == false) {
                    projects.value.data.forEach((item, index) => {
                        selectedIds.value.push(item.id);
                    })
                } else {
                    selectedIds.value = [];
                }
            }
            const select = () => {
                allSelected.value = false;
            }
            const statusChange = () => {
                let data = {
                    selectedIds: selectedIds.value
                }
                swal({
                    title: 'Are you sure?',
                    text: "You want to proceed with this action",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        axios.post(`/${backendPrefix.value}/projects/status-change`, data)
                            .then(function (response) {
                                swal.fire(
                                    'Updated!',
                                    'Your information has been Updated.',
                                    'success'
                                );
                                // Trigger the watch handler by updating the form
                                form.value.length = 15; // This will trigger the watch handler
                            });
                    }
                });
            }
            return {
                filters,
                projects,
                form,
                type,
                title,
                url,
                createUrl,
                reset,
                permission,
                amountopen,
                project_id,
                openmodal,
                update_amount_popup,
                selected,
                allSelected,
                selectedIds,
                selectAll,
                select,
                backendPrefix,
                statusChange,
                ministries,
                openHistoryModal,
                historyOpen,
                update_history_popup,
                project_name,
                filteredMinistries,
                selectMinistry,
                selectMinistry,
                ministrySearch,
                initializeMinistrySearch,
                dropdownRef,
                handleBlur,
                clearSelection,
                handleClickOutside,
                isMinistryOpen,
                filteredProposingMinistries,
                selectProposingMinistry,
                proposingMinistrySearch,
                initializeProposingMinistrySearch,
                proposingDropdownRef,
                handleProposingBlur,
                clearProposingSelection,
                isProposingMinistryOpen,
                fiscal_years
            };
        },
        watch: {
            form: {
                deep: true,
                handler: throttle(function () {
                    this.$inertia.get("/backend/projects", pickBy(this.form), {
                        preserveState: true,
                    });
                }, 150),
            },
        },
    };
</script>
<style lang="scss" scoped>
    .rounded-full {
        border-radius: 9999px;
    }

    .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.125rem 0.625rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
    }
</style>
